// SCR-STAFF-002 — حساب من (فاز 0): پروفایل (نام، موبایل فقط‌خواندنی)، تغییر رمز با Required Action کیکلاک (UPDATE_PASSWORD) و
// شعبه فعال (AC-IAM-002.2). AP-IAM-03: احراز دومرحله‌ای (CONFIGURE_TOTP، کدهای بازیابی، غیرفعال‌سازی وقتی الزامی نیست —
// US-IAM-003)، نشست‌های فعال با خروج از نشست (US-IAM-050) و کد تأیید مدیر (PIN) با سقف‌های تأیید من (US-IAM-034). AP-IAM-05: امضا و مهر خود ارائه‌دهنده (US-IAM-024).
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useAuth, useTenantName, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, KeyValueList, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApprovalPinSection, SessionsSection, TwoFactorSection } from './AccountSecurity';
import { ProviderSignatureSection } from './ProviderSignatureSection';

export const SCR_STAFF_002 = 'SCR-STAFF-002';
export const ACCOUNT_PATH = '/account';

export function AccountPage() {
  const { t } = useTranslation('iam');
  const auth = useAuth();
  const { keycloakApi, activeTenant, session, switchBranch } = useTenantSession();
  const tenantName = useTenantName();
  const [switching, setSwitching] = useState(false);

  const me = useQuery({
    queryKey: ['identity', 'me', auth.user?.subject],
    queryFn: () => unwrap(keycloakApi.GET('/api/v1/identity/me')),
    staleTime: 5 * 60_000,
  });

  const branches = activeTenant?.branches ?? [];

  return (
    <section data-screen-id={SCR_STAFF_002}>
      <PageHeader titleKey="iam:account.title" subtitleKey="iam:account.subtitle" />
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            {t('iam:account.profile')}
          </Typography>
          {me.isPending ? (
            <LoadingState rows={2} />
          ) : me.error ? (
            <ErrorState error={me.error} onRetry={() => void me.refetch()} />
          ) : (
            <KeyValueList
              items={[
                { labelKey: 'iam:account.name', value: me.data?.displayName, testId: 'account-name' },
                {
                  labelKey: 'iam:account.mobile',
                  value: me.data?.mobile ? <bdi dir="ltr">{faDigits(me.data.mobile)}</bdi> : null,
                  testId: 'account-mobile',
                },
                { labelKey: 'iam:account.tenant', value: tenantName(activeTenant) },
              ]}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t('iam:account.mobile_hint')}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {t('iam:account.password')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {t('iam:account.password_hint')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<KeyOutlinedIcon />}
            onClick={() => void auth.changePassword('/account')}
            data-testid="account-change-password"
          >
            {t('iam:account.change_password')}
          </Button>
        </Paper>

        <CalendarPreferencesSection />
        <TwoFactorSection />
        <SessionsSection />
        <ApprovalPinSection />
        <ProviderSignatureSection />

        {branches.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              {t('iam:account.branch')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1.5 }}>
              {t('iam:account.branch_hint')}
            </Typography>
            <TextField
              select
              label={t('shell:header.branch_label')}
              value={session?.activeBranchId ?? ''}
              disabled={switching}
              onChange={(event) => {
                setSwitching(true);
                void switchBranch(event.target.value).finally(() => setSwitching(false));
              }}
              sx={{ minWidth: 240 }}
              slotProps={{
                select: { displayEmpty: true },
                inputLabel: { shrink: true },
                htmlInput: { 'data-testid': 'account-branch' },
              }}
            >
              {!session?.activeBranchId && (
                <MenuItem value="" disabled>
                  {t('shell:header.branch_choose')}
                </MenuItem>
              )}
              {branches.map((branch) => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.name || t('shell:branch.unnamed')}
                </MenuItem>
              ))}
            </TextField>
          </Paper>
        )}
      </Stack>
    </section>
  );
}

function CalendarPreferencesSection() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: ['identity', session?.tenantId, 'calendar-preferences'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/me/calendar-preferences')),
    enabled: session?.tenantId !== undefined,
  });
  const save = useMutation({
    mutationFn: (body: { calendarSystem: string; defaultView: string }) =>
      unwrap(tenantApi.PUT('/api/v1/identity/me/calendar-preferences', { body })),
    onSuccess: (data) => {
      queryClient.setQueryData(['identity', session?.tenantId, 'calendar-preferences'], data);
    },
  });
  if (session?.tenantId === undefined) {
    return null;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="calendar-preferences">
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('iam:account.calendar')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.5 }}>
        {t('iam:account.calendar_hint')}
      </Typography>
      {preferences.isPending ? (
        <LoadingState rows={2} />
      ) : preferences.error || !preferences.data ? (
        <ErrorState error={preferences.error ?? new Error('calendar-preferences')} onRetry={() => void preferences.refetch()} />
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('iam:account.calendar_system')}
            value={preferences.data.calendarSystem}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ calendarSystem: event.target.value, defaultView: preferences.data?.defaultView ?? 'day' })
            }
            sx={{ minWidth: 200 }}
            data-testid="calendar-system"
          >
            <MenuItem value="Jalali">{t('iam:account.calendar_jalali')}</MenuItem>
            <MenuItem value="Gregorian">{t('iam:account.calendar_gregorian')}</MenuItem>
          </TextField>
          <TextField
            select
            label={t('iam:account.calendar_view')}
            value={preferences.data.defaultView}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ calendarSystem: preferences.data?.calendarSystem ?? 'Jalali', defaultView: event.target.value })
            }
            sx={{ minWidth: 200 }}
            data-testid="calendar-default-view"
          >
            <MenuItem value="day">{t('iam:account.calendar_day')}</MenuItem>
            <MenuItem value="week">{t('iam:account.calendar_week')}</MenuItem>
            <MenuItem value="month">{t('iam:account.calendar_month')}</MenuItem>
            <MenuItem value="list">{t('iam:account.calendar_list')}</MenuItem>
          </TextField>
        </Stack>
      )}
    </Paper>
  );
}
